import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { ServerPrivateRegionAgentInstance } from '@/api/type-server'
import { Button, Modal, Trans } from '@/components'
import { message } from '@/components/base/Message'
import { InstanceStatus } from '@/models/instance'

import { NodeStatus } from '../utils/nodeFormatters'
import { ActiveInstancesModal } from './ActiveInstancesModal'

interface StandbyRequiredModalProps {
  open: boolean
  resID: string
  onClose: () => void
  onSwitchSuccess: () => void
}

export function StandbyRequiredModal({
  open,
  resID,
  onClose,
  onSwitchSuccess,
}: StandbyRequiredModalProps) {
  const { t } = useTranslation('privateRegion')
  const [loading, setLoading] = useState(false)
  const [activeInstances, setActiveInstances] = useState<ServerPrivateRegionAgentInstance[]>([])

  const handleSwitchToStandby = async () => {
    setLoading(true)
    try {
      // Check for active instances first
      const res = await API.getPrivateRegionAgentInstances({
        res_id: resID,
        statuses: [InstanceStatus.Pending, InstanceStatus.Running],
      })
      const instances = res.instances || []
      if (instances.length > 0) {
        setActiveInstances(instances)
      } else {
        // No active instances, switch to standby
        const switchRes = await API.updatePrivateRegionNodeStatus({
          res_id: resID,
          status: NodeStatus.Standby,
        })
        if (switchRes.code !== 0) {
          message.error(t('errors.failedToSwitchStatus'))
          return
        }
        onSwitchSuccess()
      }
    } catch (err) {
      message.error(t('errors.failedToCheckInstances'))
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setActiveInstances([])
    onClose()
  }

  if (activeInstances.length > 0) {
    return <ActiveInstancesModal open={open} instances={activeInstances} onClose={handleClose} />
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <Modal.Header>
        <Trans ns="privateRegion" i18nKey="nodeDetail.standbyRequiredTitle">
          Standby Mode Required
        </Trans>
      </Modal.Header>
      <Modal.Body>
        <div className="t-medium">
          <Trans ns="privateRegion" i18nKey="nodeDetail.standbyRequiredContent">
            The server must be in Standby status to edit instance specs. Would you like to switch to
            Standby now?
          </Trans>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" onClick={handleClose} disabled={loading}>
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button type="secondary" onClick={handleSwitchToStandby} disabled={loading}>
          <Trans ns="privateRegion" i18nKey="nodeDetail.switchToStandbyAndEdit" />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
